'use client';

import { Menu, X, MoveRight } from 'lucide-react';
import { Image } from '@/hen-ui/atomics/Image';
import { NavigationWrapper } from '@/hen-ui/atomics/NavigationWrapper';

import {
  navbarVariants,
  navbarLinksVariants,
  navbarActionsVariants,
  type NavbarVariantProps,
  type NavbarLinksVariantProps,
  type NavbarActionsVariantProps,
} from './navbar.variants';
import { cn } from '@/hen-ui/utils';
import { Flex } from '@/hen-ui/composites/Flex';
import type { FlexVariantProps } from '@/hen-ui/composites/Flex/flex.variants';
import { Button } from '@/hen-ui/atomics/Button';
import { Link } from '@/hen-ui/atomics/Link';
import {
  Drawer,
  DrawerContent,
  DrawerTrigger,
  DrawerClose,
  DrawerHeader,
} from '@/hen-ui/composites/Drawer';
import { forwardRef, useState } from 'react';

export interface NavbarProps
  extends React.HTMLAttributes<HTMLElement>,
    NavbarVariantProps {
  /**
   * Content to display in the logo area
   */
  logoSrc?: string;
  /**
   * Navigation destination for the logo (makes logo clickable)
   */
  logoHref?: string;
  /**
   * Links to display in the navigation
   */
  links?: NavbarLinkItem[];
  /**
   * Actions to display (e.g., buttons, user menu)
   */
  actions?: React.ReactNode;

  /**
   * Custom drawer title for mobile menu (for accessibility)
   * @default 'Navigation Menu'
   */
  drawerTitle?: string;
  /**
   * Horizontal alignment of navbar content
   * @default 'between'
   */
  justify?: FlexVariantProps['justify'];
}

export interface NavbarLinkItem {
  /**
   * Link label
   */
  label: string;
  /**
   * Link href
   */
  href: string;
  /**
   * Whether the link is active
   */
  active?: boolean;
  /**
   * Click handler
   */
  onClick?: (e: React.MouseEvent<HTMLAnchorElement>) => void;
}

export interface NavbarLinksProps
  extends React.HTMLAttributes<HTMLDivElement>,
    NavbarLinksVariantProps {}

export interface NavbarLinkProps
  extends React.AnchorHTMLAttributes<HTMLAnchorElement> {
  item: NavbarLinkItem;
}

export interface NavbarActionsProps
  extends React.HTMLAttributes<HTMLDivElement>,
    NavbarActionsVariantProps {}

/**
 * Navbar component following HEN Design System standards.
 *
 * A responsive navigation bar that automatically switches to a drawer menu on mobile.
 * Supports logo, navigation links, and action buttons with full customization.
 *
 * @example
 * // Basic navbar
 * <Navbar
 *   logoSrc="/logo.svg"
 *   links={[
 *     { label: 'Home', href: '/', active: true },
 *     { label: 'About', href: '/about' },
 *     { label: 'Contact', href: '/contact' },
 *   ]}
 *   actions={<Button variant="primaryFill">Sign In</Button>}
 * />
 *
 */
export const Navbar = forwardRef<HTMLElement, NavbarProps>(
  (
    {
      className,
      variant,
      sticky,
      justify = 'between',
      logoSrc,
      logoHref,
      links = [],
      actions,
      drawerTitle = 'Navigation Menu',
      ...props
    },
    ref,
  ) => {
    const [isDrawerOpen, setIsDrawerOpen] = useState(false);

    const LogoImage = logoSrc ? (
      <Image
        src={logoSrc}
        alt='Logo'
        className='flex-shrink-0 w-[200px] md:w-[260px]'
        objectFit='contain'
      />
    ) : null;

    return (
      <nav
        ref={ref}
        className={cn(navbarVariants({ variant, sticky }), className)}
        {...props}
      >
        <Flex
          direction='row'
          justify={justify}
          align='center'
          gap='none'
          className=' gap-10 h-16 md:h-24 px-8'
        >
          <div className='flex items-center justify-center md:gap-8'>
            {/* Logo */}
            {LogoImage &&
              (logoHref ? (
                <NavigationWrapper href={logoHref} aria-label='Go to homepage'>
                  {LogoImage}
                </NavigationWrapper>
              ) : (
                LogoImage
              ))}

            {/* Desktop Links */}
            {links.length > 0 && (
              <NavbarLinks
                orientation='horizontal'
                className={`hidden sm:flex`}
              >
                {links.map((link, index) => (
                  <NavbarLink
                    key={`${link.href}-${index}`}
                    item={link}
                    className='font-bold'
                  />
                ))}
              </NavbarLinks>
            )}
          </div>

          {/* Desktop Actions */}
          <NavbarActions orientation='horizontal' className={`hidden lg:flex`}>
            {actions}
          </NavbarActions>

          {/* Mobile Drawer */}
          <Drawer
            open={isDrawerOpen}
            direction='top'
            onOpenChange={setIsDrawerOpen}
          >
            <DrawerTrigger asChild className={`flex sm:hidden`}>
              <Button
                variant='textOnly'
                className='p-0 stroke-gray-700 [&_svg]:size-6'
                aria-label='Open navigation menu'
              >
                <Menu size={24} />
              </Button>
            </DrawerTrigger>
            <DrawerContent size='full'>
              <DrawerHeader className='flex flex-row items-center justify-end p-6'>
                <DrawerClose asChild>
                  <Button
                    variant='textOnly'
                    className='flex p-2'
                    aria-label='Close navigation menu'
                  >
                    <X className='flex-shrink-0 stroke-gray-700' size={'4px'} />
                  </Button>
                </DrawerClose>
              </DrawerHeader>

              <div className='flex flex-col gap-6 p-6'>
                {/* Mobile Links */}
                {links.length > 0 && (
                  <NavbarLinks orientation='vertical'>
                    {links.map((link, index) => (
                      <NavbarLink
                        key={`mobile-${link.href}-${index}`}
                        item={link}
                        className='font-bold'
                        onClick={(e) => {
                          setIsDrawerOpen(false);
                          link.onClick?.(e);
                        }}
                      />
                    ))}
                  </NavbarLinks>
                )}

                {/* Mobile Actions */}
                {actions && (
                  <NavbarActions orientation='vertical'>
                    {actions}
                  </NavbarActions>
                )}
              </div>
            </DrawerContent>
          </Drawer>
        </Flex>
      </nav>
    );
  },
);

Navbar.displayName = 'Navbar';

/**
 * NavbarLinks - Container for navigation links
 */
export const NavbarLinks = forwardRef<HTMLDivElement, NavbarLinksProps>(
  ({ className, orientation, children, ...props }, ref) => {
    return (
      <div
        ref={ref}
        className={cn(navbarLinksVariants({ orientation }), className)}
        {...props}
      >
        {children}
      </div>
    );
  },
);

NavbarLinks.displayName = 'NavbarLinks';

/**
 * NavbarLink - Individual navigation link
 */
export const NavbarLink = forwardRef<HTMLAnchorElement, NavbarLinkProps>(
  ({ className, item, onClick, ...props }, ref) => {
    const handleClick = (e: React.MouseEvent<HTMLAnchorElement>) => {
      onClick?.(e);
      item.onClick?.(e);
    };

    return (
      <Link
        ref={ref}
        href={item.href}
        className={cn(
          `text-xl sm:text-sm font-medium text-black transition-colors active:text-primary-700 hover:text-gray-700 hover:decoration-2 hover:decoration-neutral-100 underline-offset-8 w-full flex items-center justify-between py-2`,
          className,
        )}
        onClick={handleClick}
        {...props}
      >
        {item.label}
        <MoveRight className='ml-1 inline-block sm:hidden' size={24} />
      </Link>
    );
  },
);

NavbarLink.displayName = 'NavbarLink';

/**
 * NavbarActions - Container for action buttons/elements
 */
export const NavbarActions = forwardRef<HTMLDivElement, NavbarActionsProps>(
  ({ className, orientation, children, ...props }, ref) => {
    return (
      <div
        ref={ref}
        className={cn(navbarActionsVariants({ orientation }), className)}
        {...props}
      >
        {children}
      </div>
    );
  },
);

NavbarActions.displayName = 'NavbarActions';
